import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PlusCircle, List, Table, Columns2, Loader2, Copy, ClipboardPaste } from 'lucide-react';
import { SaveStatusIndicator } from '@/components/save/SaveStatusIndicator';
import { SaveErrorBanner } from '@/components/save/SaveErrorBanner';
import { UndoRedoButtons } from '@/components/breakdown-chrome/UndoRedoButtons';
import { PreviewVersionChip } from '@/components/breakdown-chrome/PreviewVersionChip';
import { useBreakdownChrome } from '@/hooks/useBreakdownChrome';
import { Panel } from '@/components/ui/panel';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { type LineItemData, type ProjectData } from './LineItemList';
import { BreakdownDndWrapper } from './BreakdownDndWrapper';
import { useFlatItems } from './useFlatItems';
import { toFlatItems, fromFlatItems, type FlatItem } from './types';
import { TemplateLineItems, TemplateItem } from './TemplateLineItems';
import { BreakdownAddActions } from './BreakdownAddActions';
import { Combobox } from '@/components/ui/combobox';
import { downloadResponseAsFile } from '@/lib/utils';
import { LineItemDetail } from './LineItemDetail';
import { BreakdownTableView } from './BreakdownTableView';
import { DropdownButtonMenu } from '@/components/ui/dropdown-button-menu';
import {
  type SubItemForm,
  createSubItem,
  isQtyRateType,
  getSubItemTypeOptions,
} from './subItemTypes';
import {
  calculateTotalCost,
  type Rates,
  DEFAULT_TAX_RATE,
  DEFAULT_MARKUP_RATE,
} from '@jobos/shared';
import { useBreakdown, breakdownKeys } from '@/hooks/useBreakdown';
import { CarpetLineItemBody } from '../carpet/CarpetLineItemBody';
import { EquipmentLineItemBody } from '../equipment/EquipmentLineItemBody';
import { useCategorySubItemDefaults } from '@/hooks/useCategorySubItemDefaults';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useCommonTemplateItems } from '@/hooks/useCommonTemplateItems';
import { useBreakdownTemplates } from '@/hooks/useBreakdownTemplates';
import {
  applyTemplateSnapshot,
  snapshotHasRates,
  type ApplyTemplateMode,
} from './applyTemplateSnapshot';
import { ApplyTemplateConfirm } from './ApplyTemplateConfirm';
import { flatItemsToContents } from './templateConversions';
import type { BreakdownPatch, BreakdownTemplate, CommonTemplateItem } from '@jobos/shared';
import { projectTypeToSlotKey } from '@jobos/shared';
import { VersionDropdown } from '@/components/breakdown-chrome/VersionDropdown';
import { VersionSwitcher } from './VersionSwitcher';
import { DocumentBanner } from './DocumentBanner';
import { RenameDocumentDialog } from './RenameDocumentDialog';
import { useBreakdownDocuments } from '@/hooks/useBreakdownDocuments';
import { RecoverEditsDialog } from '@/components/breakdown-chrome/RecoverEditsDialog';
import { RestoreConfirmDialog } from '@/components/breakdown-chrome/RestoreConfirmDialog';
import { apiClient } from '@/api/client';
import { SubItemCategoriesProvider, useSubItemCategoriesContext, useSubItemTypeMeta } from '@/contexts/SubItemCategoriesContext';
import { useRegisterBreakdownFlush } from '@/contexts/BreakdownFlushContext';
import { useUndoHistory } from './useUndoHistory';
import { useBreakdownVersionPreview, type VersionSnapshot } from '@/hooks/useBreakdownVersionPreview';
import { JobRatesDisplay } from './JobRatesDisplay';
import { useBreakdownSave } from './useBreakdownSave';
import { serializeForClipboard, parseClipboard, cloneAndTransformItems } from './clipboardUtils';
import { computeSelectAllState, getAllSelectableIds } from './selectionUtils';
import { PartialInvoiceDialog } from '../billing/PartialInvoiceDialog';
import { FinalInvoiceDialog } from '../billing/FinalInvoiceDialog';
import { JobCardPreviewModal } from '../overview/JobCardPreviewModal';
import { downloadJobCardXlsx } from '@/lib/jobCardDownload';

type ViewMode = 'list' | 'columns' | 'table';

interface JobDetailBreakdownProps {
  jobId: string;
  jobCode: string;
  jobProjectType?: string | null;
  activeBreakdownVersionId?: string | null;
  breakdownModifiedSinceVersion?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  // When set (BREAKDOWN_DOCS_ENABLED on, resolved by BreakdownDocsWrapper),
  // saves target the document-contents endpoint and the version chrome is
  // swapped from the legacy VersionDropdown to the new VersionSwitcher.
  documentId?: string;
}


function TemplatePopover({
  onAddItem,
  usedTemplateIds,
  commonItems,
  fullTemplates,
  onApplyFullTemplate,
  jobProjectType,
}: {
  onAddItem: (item: TemplateItem) => void;
  usedTemplateIds: Set<string>;
  commonItems?: CommonTemplateItem[];
  fullTemplates?: BreakdownTemplate[];
  onApplyFullTemplate?: (template: BreakdownTemplate) => void;
  jobProjectType?: string | null;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors">
          <PlusCircle className="h-3.5 w-3.5" />
          From Template...
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0 max-h-96 overflow-auto">
        <TemplateLineItems
          onAddItem={onAddItem}
          usedTemplateIds={usedTemplateIds}
          commonItems={commonItems}
          fullTemplates={fullTemplates}
          onApplyFullTemplate={onApplyFullTemplate}
          jobProjectType={jobProjectType}
        />
      </PopoverContent>
    </Popover>
  );
}

export function JobDetailBreakdown(props: JobDetailBreakdownProps) {
  return (
    <SubItemCategoriesProvider>
      <JobDetailBreakdownInner {...props} />
    </SubItemCategoriesProvider>
  );
}

function JobDetailBreakdownInner({ jobId, jobCode, jobProjectType, activeBreakdownVersionId, breakdownModifiedSinceVersion, onDirtyChange, documentId }: JobDetailBreakdownProps) {
  const { categories, isLoading: categoriesLoading } = useSubItemCategoriesContext();
  const typeMeta = useSubItemTypeMeta();
  const queryClient = useQueryClient();
  const { data: breakdown, isLoading, error } = useBreakdown(jobId, documentId);
  const { data: commonItems = [] } = useCommonTemplateItems();
  const { data: categoryDefaults = [] } = useCategorySubItemDefaults();
  const isOnline = useOnlineStatus();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedSubItemId, setSelectedSubItemId] = useState<string | null>(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [jobCardPreviewOpen, setJobCardPreviewOpen] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState<'summary' | 'full' | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('columns');
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [finalDialogOpen, setFinalDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [autoFocusItemId, setAutoFocusItemId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
  const {
    flatItems,
    collapsedProjects,
    getVisibleSortableIds,
    initialize: initFlatItems,
    moveItem,
    moveProject,
    startProjectDrag,
    cancelProjectDrag,
    toggleCollapse,
    addLineItem: flatAddLineItem,
    deleteLineItem: flatDeleteLineItem,
    updateLineItem: flatUpdateLineItem,
    addProject: flatAddProject,
    deleteProject: flatDeleteProject,
    renameProject: flatRenameProject,
    getDndPatches,
    insertItems: flatInsertItems,
  } = useFlatItems();
  const [jobRates, setJobRates] = useState<Rates>({ taxRate: DEFAULT_TAX_RATE, markupRate: DEFAULT_MARKUP_RATE });
  const [roundingPrecision, setRoundingPrecision] = useState<number | null>(null);

  // ?docId navigation handle for the in-editor VersionSwitcher and Banner.
  // Only meaningful when documentId is set (flag-on path); the legacy chrome
  // doesn't touch search params.
  const [, setSearchParams] = useSearchParams();
  const handleNavigateDocId = useCallback(
    (docId: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (docId) {
          next.set('docId', docId);
        } else {
          next.delete('docId');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Slot this surface is mounted for — scopes the document-list cache per slot.
  const slotKey = projectTypeToSlotKey(jobProjectType);
  // Document metadata for the banner (name + Active marker). Cheap because
  // the wrapper already populated the list query — this is a cache hit.
  const { data: documentsListForBanner } = useBreakdownDocuments(
    documentId ? jobId : undefined,
    slotKey,
  );
  const currentDocSummary = documentId
    ? documentsListForBanner?.find((d) => d.id === documentId)
    : undefined;
  // Invoice generation is gated to the Active document. Showing it on
  // non-active docs invites users to bill from a snapshot/draft that isn't
  // the live state, which is confusing. Legacy (flag-off) path has no
  // documentId — treat that as "always enabled" so behavior is unchanged.
  const canCreateInvoices = !documentId || currentDocSummary?.isActive === true;

  const undoHistory = useUndoHistory();

  // Track if initial data has been loaded (to avoid overwriting local state after saves)
  const initialLoadCompleteRef = useRef(false);

  // Track which template items have been added (by their template ID)
  const [usedTemplateIds, setUsedTemplateIds] = useState<Set<string>>(new Set());
  // Map line item IDs to their source template IDs (for restoring templates on delete)
  const [lineItemToTemplateMap, setLineItemToTemplateMap] = useState<Map<string, string>>(new Map());

  // Full job type templates, offered in the "From Template..." popover.
  const { data: allBreakdownTemplates = [] } = useBreakdownTemplates();
  const fullTemplates = useMemo(
    () => allBreakdownTemplates.filter((t) => t.isActive),
    [allBreakdownTemplates],
  );
  // Set while the replace/append confirm is open (non-empty breakdown only).
  const [pendingTemplate, setPendingTemplate] = useState<BreakdownTemplate | null>(null);

  const {
    previewingVersion,
    restoreDialogOpen,
    setRestoreDialogOpen,
    isPreviewMode,
    versionLabel,
    markEdited,
    handleSelectVersion,
    handleExitPreview,
    handleRestoreSuccess,
    handleVersionSaved,
  } = useBreakdownVersionPreview({
    jobId,
    activeBreakdownVersionId,
    breakdownModifiedSinceVersion,
    onSelectVersion: useCallback((version: VersionSnapshot) => {
      const previewFlat = toFlatItems(version.snapshot);
      const firstItem = previewFlat.find((i) => i.type === 'line-item');
      if (firstItem) setSelectedItemId(firstItem.id);
    }, [setSelectedItemId]),
    onExitPreview: useCallback(() => {
      const firstItem = flatItems.find((i) => i.type === 'line-item');
      if (firstItem) setSelectedItemId(firstItem.id);
    }, [flatItems, setSelectedItemId]),
    onAfterRestore: useCallback(() => {
      undoHistory.clear();
      initialLoadCompleteRef.current = false;
    }, [undoHistory]),
  });

  const {
    isDirty,
    isSaving,
    hasOfflineEdits,
    saveError,
    save,
    flushPendingSave,
    markDirty,
    downloadBackup,
    patchBreakdown,
    pendingRecovery,
    acceptRecovery,
    discardRecovery,
  } = useBreakdownSave({
    jobId,
    flatItems,
    jobRates,
    roundingPrecision,
    initFlatItems,
    undoHistory,
    isPreviewMode,
    setSelectedItemId,
    setJobRates,
    markEdited,
    initialLoadCompleteRef,
    documentId,
  });

  // Register a flush handle so JobDetail's leave paths persist pending edits
  // to the server before navigating. flushPendingSave saves when dirty and
  // no-ops when clean — autosave-on-leave.
  useRegisterBreakdownFlush(
    () => ({
      flush: async () => {
        if (isPreviewMode) handleExitPreview();
        await flushPendingSave();
      },
    }),
    [isPreviewMode, handleExitPreview, flushPendingSave],
  );

  // Clear query cache on unmount so next mount fetches fresh data
  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: breakdownKeys.detail(jobId) });
    };
  }, [queryClient, jobId]);

  // Load data from API on initial load
  useEffect(() => {
    if (breakdown && breakdown.lineItems && !categoriesLoading && !initialLoadCompleteRef.current) {
      initialLoadCompleteRef.current = true;

      // Extract rates from API response
      if (breakdown.taxRate !== undefined && breakdown.markupRate !== undefined) {
        setJobRates({ taxRate: breakdown.taxRate, markupRate: breakdown.markupRate });
      }
      if (breakdown.roundingPrecision !== undefined) {
        setRoundingPrecision(breakdown.roundingPrecision ?? null);
      }
      // Recalculate totalCost for all line items using current rates & categories
      // (stored totalCost may be stale if category settings like markupApplicable changed)
      const currentRates = (breakdown.taxRate !== undefined && breakdown.markupRate !== undefined)
        ? { taxRate: breakdown.taxRate, markupRate: breakdown.markupRate }
        : jobRates;
      const initialFlat = toFlatItems(breakdown).map(item => {
        if (item.type === 'line-item') {
          const li = item.data as LineItemData;
          return { ...item, data: { ...li, totalCost: calculateTotalCost(li.subItems, currentRates, typeMeta, categories) } };
        }
        return item;
      });

      // Initialize flat items from the recalculated data
      initFlatItems(initialFlat);

      const allLineItems = initialFlat.filter(i => i.type === 'line-item').map(i => i.data as LineItemData);

      undoHistory.clear();
      const templateIds = new Set<string>();
      const templateMap = new Map<string, string>();
      allLineItems.forEach((item) => {
        if (item.templateId) {
          templateIds.add(item.templateId);
          templateMap.set(item.id, item.templateId);
        }
      });
      setUsedTemplateIds(templateIds);
      setLineItemToTemplateMap(templateMap);

      // Select first item if none selected
      const firstItem = allLineItems[0];
      if (firstItem && !selectedItemId) {
        setSelectedItemId(firstItem.id);
      }
    }
  }, [breakdown, categories, categoriesLoading]);

  // Persistence helper. The legacy granular PATCH /breakdown endpoint is
  // disabled when BREAKDOWN_DOCS_ENABLED is on (it returns 410), so on the
  // flag-on path we route every "send a patch to the server" call site
  // through the debounced autosave instead. The flag-off path keeps the
  // original behavior — patches go directly to the legacy endpoint for
  // immediate persistence.
  const persistGranular = useCallback((patches: BreakdownPatch[]) => {
    if (documentId) {
      markDirty();
    } else if (patches.length > 0) {
      patchBreakdown.mutate({ patches });
    }
  }, [documentId, markDirty, patchBreakdown]);

  const handleAddLineItem = () => {
    markDirty();
    const newItemId = crypto.randomUUID();
    const newItem: LineItemData = {
      id: newItemId,
      category: '',
      description: '',
      totalCost: 0,
      subItems: [],
    };
    let afterId: string | undefined;
    if (viewMode === 'table') {
      // Bottom-most row that is checked or expanded.
      const active = new Set<string>([...checkedIds, ...expandedItemIds]);
      for (let i = flatItems.length - 1; i >= 0; i--) {
        const fi = flatItems[i];
        if (fi && fi.type === 'line-item' && active.has(fi.id)) {
          afterId = fi.id;
          break;
        }
      }
    } else {
      afterId = selectedItemId ?? undefined;
    }
    flatAddLineItem(newItem, undefined, afterId);
    setSelectedItemId(newItemId);
    setAutoFocusItemId(newItemId);
  };

  const handleAddTemplateItem = (template: TemplateItem) => {
    markDirty();
    const newItemId = crypto.randomUUID();
    // Auto-populate sub-items from category defaults
    const defaults = categoryDefaults.filter((d) => d.lineItemCategory === template.category);
    let subItems: SubItemForm[] = [];
    let totalCost = template.defaultCost ?? 0;
    if (defaults.length > 0) {
      subItems = defaults.map((d) => {
        const id = crypto.randomUUID();
        if (isQtyRateType(d.subItemCategoryKey, typeMeta)) {
          return {
            id,
            type: d.subItemCategoryKey,
            hours: d.defaultHours ?? 0,
            rate: d.defaultRate ?? 0,
            rateType: d.defaultRateType ?? undefined,
            note: d.defaultNote ?? '',
          } as SubItemForm;
        }
        return {
          id,
          type: d.subItemCategoryKey,
          amount: d.defaultAmount ?? 0,
          note: d.defaultNote ?? '',
        } as SubItemForm;
      });
      totalCost = calculateTotalCost(subItems, jobRates, typeMeta, categories);
    }
    const newItem: LineItemData = {
      id: newItemId,
      category: template.category,
      description: template.description,
      totalCost,
      subItems,
      templateId: template.isReusable ? undefined : template.id,
    };
    flatAddLineItem(newItem, undefined, selectedItemId ?? undefined);
    if (!template.isReusable) {
      setUsedTemplateIds((ids) => new Set([...ids, template.id]));
      setLineItemToTemplateMap((map) => new Map([...map, [newItemId, template.id]]));
    }
    setSelectedItemId(newItemId);
  };

  const handleDeleteLineItem = (id: string) => {
    // Don't call markDirty() here — the direct delete patch below
    // handles persistence.
    // Check if this line item came from a template
    const templateId = lineItemToTemplateMap.get(id);
    if (templateId) {
      // Restore the template to the available list
      setUsedTemplateIds((ids) => {
        const newIds = new Set(ids);
        newIds.delete(templateId);
        return newIds;
      });
      // Remove from the mapping
      setLineItemToTemplateMap((map) => {
        const newMap = new Map(map);
        newMap.delete(id);
        return newMap;
      });
    }
    flatDeleteLineItem(id);
    // Send delete patch directly for immediate persistence (flag-off) or
    // mark dirty so the autosave full-replace catches up (flag-on).
    persistGranular([{ op: 'deleteLineItem', id }]);

    // Clear selection if deleted item was selected
    if (selectedItemId === id) {
      setSelectedItemId(null);
    }
  };

  const handleLineItemCategoryChange = (id: string, category: string) => {
    markDirty();
    flatUpdateLineItem(id, (item) => {
      // Only auto-populate if item has no sub-items
      if (item.subItems.length > 0) return { ...item, category };
      const defaults = categoryDefaults.filter((d) => d.lineItemCategory === category);
      if (defaults.length === 0) return { ...item, category };
      const subItems: SubItemForm[] = defaults.map((d) => {
        const subId = crypto.randomUUID();
        if (isQtyRateType(d.subItemCategoryKey, typeMeta)) {
          return {
            id: subId,
            type: d.subItemCategoryKey,
            hours: d.defaultHours ?? 0,
            rate: d.defaultRate ?? 0,
            rateType: d.defaultRateType ?? undefined,
            note: d.defaultNote ?? '',
          } as SubItemForm;
        }
        return {
          id: subId,
          type: d.subItemCategoryKey,
          amount: d.defaultAmount ?? 0,
          note: d.defaultNote ?? '',
        } as SubItemForm;
      });
      const totalCost = calculateTotalCost(subItems, jobRates, typeMeta, categories);
      return { ...item, category, subItems, totalCost };
    });
  };

  const handleLineItemDescriptionChange = (id: string, description: string) => {
    markDirty();
    flatUpdateLineItem(id, (item) => ({ ...item, description }));
  };

  const handleLineItemDateChange = (id: string, itemDate: string) => {
    markDirty();
    flatUpdateLineItem(id, (item) => ({ ...item, itemDate: itemDate || undefined }));
  };

  const handleSubItemsChange = (subItems: SubItemForm[]) => {
    if (!selectedItemId) return;
    markDirty();
    if (selectedSubItemId && !subItems.some(s => s.id === selectedSubItemId)) {
      setSelectedSubItemId(null);
    }
    const totalCost = calculateTotalCost(subItems, jobRates, typeMeta, categories);
    flatUpdateLineItem(selectedItemId, (item) => ({ ...item, subItems, totalCost }));
  };

  const handleAddSubItem = (type: string) => {
    if (!selectedItemId) return;
    markDirty();
    const newItem = createSubItem(type, typeMeta);
    setSelectedSubItemId(newItem.id);
    flatUpdateLineItem(selectedItemId, (item) => {
      const subItems = [...item.subItems, newItem];
      return { ...item, subItems, totalCost: calculateTotalCost(subItems, jobRates, typeMeta, categories) };
    });
  };

  // Project handlers
  const handleAddProject = () => {
    const newProjectId = crypto.randomUUID();
    const sortOrder = flatItems.filter(i => i.projectId === null).length;
    const newProject: ProjectData = {
      id: newProjectId,
      name: 'New Project',
      sortOrder,
      lineItems: [],
    };
    flatAddProject(newProject, selectedItemId ?? undefined);
    persistGranular([{
      op: 'createProject',
      id: newProjectId,
      data: { name: 'New Project', sortOrder },
    }]);
  };

  const handleRenameProject = (projectId: string, name: string) => {
    flatRenameProject(projectId, name);
    persistGranular([{
      op: 'updateProject',
      id: projectId,
      field: 'name',
      value: name,
    }]);
  };

  const handleDeleteProject = (projectId: string) => {
    flatDeleteProject(projectId);
    persistGranular([{
      op: 'deleteProject',
      id: projectId,
    }]);
  };

  const handleAddLineItemToProject = (projectId: string) => {
    // Don't call markDirty() — the direct create patch below handles persistence.
    const newItemId = crypto.randomUUID();
    const newItem: LineItemData = {
      id: newItemId,
      category: '',
      description: '',
      totalCost: 0,
      subItems: [],
    };
    flatAddLineItem(newItem, projectId);
    setSelectedItemId(newItemId);
    setAutoFocusItemId(newItemId);
    const projectMembers = flatItems.filter(i => i.projectId === projectId);
    persistGranular([{
      op: 'createLineItem',
      id: newItemId,
      data: { category: '', sortOrder: projectMembers.length, projectId },
    }]);
  };

  // Toggle handler for accordion expand/collapse
  const handleToggleExpand = useCallback((id: string | null) => {
    if (id === null) {
      setSelectedItemId(null);
    } else {
      setSelectedItemId((current) => current === id ? null : id);
    }
    setSelectedSubItemId(null);
  }, []);

  const handleCheckItem = useCallback((id: string, checked: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleCheckProject = useCallback((projectId: string, checked: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(projectId);
        // Also check all members
        flatItems.filter((i) => i.projectId === projectId).forEach((i) => next.add(i.id));
      } else {
        next.delete(projectId);
        // Also uncheck all members
        flatItems.filter((i) => i.projectId === projectId).forEach((i) => next.delete(i.id));
      }
      return next;
    });
  }, [flatItems]);

  // Derive indeterminate state for projects
  const getProjectCheckState = useCallback((projectId: string) => {
    const members = flatItems.filter((i) => i.projectId === projectId);
    const checkedMembers = members.filter((i) => checkedIds.has(i.id));
    const isChecked = checkedIds.has(projectId) || (members.length > 0 && checkedMembers.length === members.length);
    const isIndeterminate = checkedMembers.length > 0 && checkedMembers.length < members.length;
    return { isChecked, isIndeterminate };
  }, [flatItems, checkedIds]);

  const selectAllState = useMemo(
    () => computeSelectAllState(flatItems, checkedIds),
    [flatItems, checkedIds],
  );

  const handleSelectAll = useCallback(() => {
    // Gmail semantics: any non-empty selection clears; empty selects all.
    setCheckedIds((prev) => (prev.size > 0 ? new Set() : getAllSelectableIds(flatItems)));
  }, [flatItems]);

  // Handle per-job rate changes
  const handleRatesChange = useCallback((newRates: { taxRate: number; markupRate: number }) => {
    markDirty();
    setJobRates(newRates);
    // Recalculate all line item totals with new rates
    flatItems.forEach(item => {
      if (item.type === 'line-item') {
        flatUpdateLineItem(item.id, (li) => ({
          ...li,
          totalCost: calculateTotalCost(li.subItems, newRates, typeMeta, categories),
        }));
      }
    });
  }, [markDirty, flatItems, flatUpdateLineItem, typeMeta, categories]);


  // Apply a full job type template into the open breakdown. Client-side by
  // design: pushing the converted snapshot through initFlatItems lets the
  // existing autosave diff persist it and keeps the operation undoable.
  const applyFullTemplate = useCallback(
    (template: BreakdownTemplate, mode: ApplyTemplateMode) => {
      // markDirty() first so the pre-apply state lands in undo history — same
      // ordering handleRatesChange and handlePaste rely on.
      markDirty();
      const next = applyTemplateSnapshot({
        snapshot: template.snapshot,
        mode,
        existingFlatItems: flatItems,
        currentRates: jobRates,
        currentRounding: roundingPrecision,
        typeMeta,
        categories,
      });
      initFlatItems(next.flatItems);
      setJobRates(next.rates);
      setRoundingPrecision(next.roundingPrecision);

      // Rebuild the common-line-item bookkeeping from the new items — otherwise
      // the "From Template" list filters against ids that are no longer present.
      const lineItems = next.flatItems
        .filter((i) => i.type === 'line-item')
        .map((i) => i.data as LineItemData);
      const templateIds = new Set<string>();
      const templateMap = new Map<string, string>();
      lineItems.forEach((li) => {
        if (li.templateId) {
          templateIds.add(li.templateId);
          templateMap.set(li.id, li.templateId);
        }
      });
      setUsedTemplateIds(templateIds);
      setLineItemToTemplateMap(templateMap);
      setSelectedItemId(lineItems[0]?.id ?? null);
      setPendingTemplate(null);
    },
    [markDirty, flatItems, jobRates, roundingPrecision, typeMeta, categories, initFlatItems],
  );

  const handleSelectFullTemplate = useCallback(
    (template: BreakdownTemplate) => {
      // An empty breakdown has nothing to lose — skip the dialog.
      if (flatItems.length === 0) {
        applyFullTemplate(template, 'replace');
        return;
      }
      setPendingTemplate(template);
    },
    [flatItems.length, applyFullTemplate],
  );

  const handleSetRounding = (precision: number | null) => {
    undoHistory.flush();
    markDirty();
    setRoundingPrecision(precision);
    undoHistory.flush();
  };

  // computeItemTotal is defined after displayedRates (below) so it uses the correct rates in version preview mode

  // Both job card actions flush pending edits first, so the card reflects what
  // the user just typed. They pass the doc being viewed so the card comes from
  // THIS doc, not the slot's active one (documentId is undefined on the legacy
  // flag-off path, which falls back to the active doc).
  const handlePreviewJobCard = async () => {
    setIsExportingExcel(true);
    try {
      if (isDirty) await save();
      setJobCardPreviewOpen(true);
    } catch (error) {
      console.error('Error preparing job card preview:', error);
      alert('Failed to prepare the job card. Please try again.');
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      if (isDirty) await save();
      await downloadJobCardXlsx({ jobId, jobCode, documentId });
    } catch (error) {
      console.error('Error exporting breakdown to Excel:', error);
      alert('Failed to export breakdown. Please try again.');
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportPdf = async (reportType: 'summary' | 'full') => {
    setIsExportingPdf(reportType);
    try {
      if (isDirty) await save();
      const response = await apiClient.post<Response>('/breakdown-pdf', { jobId, reportType, documentId });
      await downloadResponseAsFile(response, `Estimate_${jobCode}.pdf`);
    } catch (error) {
      console.error('Error exporting breakdown to PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingPdf(null);
    }
  };

  // Opens the FinalInvoiceDialog. The previous one-click flow used the
  // active doc's total as a non-overridable amount and didn't handle the
  // existing-final case; the dialog covers both. We flush any pending
  // breakdown save first so the source-doc rates/totals captured at invoice
  // creation reflect the user's latest edits.
  const handleCreateFinalInvoice = async () => {
    if (isDirty) await save();
    setFinalDialogOpen(true);
  };

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (isPreviewMode) return;
    const snapshot = undoHistory.undo({ flatItems, jobRates, roundingPrecision });
    if (!snapshot) return;
    initFlatItems(snapshot.flatItems);
    setJobRates(snapshot.jobRates);
    setRoundingPrecision(snapshot.roundingPrecision);
    markDirty(true);
  }, [undoHistory, flatItems, jobRates, roundingPrecision, isPreviewMode, initFlatItems, markDirty]);

  const handleRedo = useCallback(() => {
    if (isPreviewMode) return;
    const snapshot = undoHistory.redo({ flatItems, jobRates, roundingPrecision });
    if (!snapshot) return;
    initFlatItems(snapshot.flatItems);
    setJobRates(snapshot.jobRates);
    setRoundingPrecision(snapshot.roundingPrecision);
    markDirty(true);
  }, [undoHistory, flatItems, jobRates, roundingPrecision, isPreviewMode, initFlatItems, markDirty]);

  const handleCopy = useCallback(async () => {
    if (checkedIds.size === 0) return;

    // Build the items to copy — projects include only checked members
    const itemsToCopy: FlatItem[] = [];
    for (const item of flatItems) {
      if (item.type === 'project-header' && checkedIds.has(item.id)) {
        itemsToCopy.push(item);
      } else if (item.type === 'line-item' && checkedIds.has(item.id)) {
        itemsToCopy.push(item);
      }
    }

    const json = serializeForClipboard(itemsToCopy);
    await navigator.clipboard.writeText(json);
    setCheckedIds(new Set());
  }, [checkedIds, flatItems]);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const result = parseClipboard(text);
      if (!result.ok) {
        return;
      }
      if (result.items.length === 0) return;

      // Snapshot for undo
      undoHistory.markDirty({ flatItems, jobRates, roundingPrecision });

      const transformed = cloneAndTransformItems(result.items, jobRates, typeMeta, categories);
      flatInsertItems(transformed, selectedItemId ?? undefined);
      markDirty();

      // Select the first pasted item
      if (transformed.length > 0) {
        setSelectedItemId(transformed[0]!.id);
      }
    } catch (err) {
      console.error('Paste failed:', err);
    }
  }, [flatItems, jobRates, typeMeta, categories, selectedItemId, undoHistory, markDirty, flatInsertItems]);

  useBreakdownChrome({
    isDirty,
    isSaving,
    isPreviewMode,
    onSave: save,
    onUndo: handleUndo,
    onRedo: handleRedo,
    onDirtyChange,
  });

  // Derive loose items and projects from flatItems for backward compatibility
  const { looseItems: currentLooseItems, projects: currentProjects } = useMemo(
    () => fromFlatItems(flatItems),
    [flatItems]
  );

  const visibleSortableIds = useMemo(() => getVisibleSortableIds(), [getVisibleSortableIds, flatItems, collapsedProjects]);

  // Compute displayed line items, projects, and rates (preview or current)
  const previewFlatItems = useMemo(
    () => previewingVersion ? toFlatItems(previewingVersion.snapshot) : [],
    [previewingVersion]
  );
  const { looseItems: previewLooseItems, projects: previewProjects } = useMemo(
    () => previewingVersion ? fromFlatItems(previewFlatItems) : { looseItems: [], projects: [] },
    [previewingVersion, previewFlatItems]
  );
  const displayedLineItems = isPreviewMode ? previewLooseItems : currentLooseItems;
  const displayedProjects = isPreviewMode ? previewProjects : currentProjects;
  const displayedFlatItems = isPreviewMode ? previewFlatItems : flatItems;
  const displayedVisibleSortableIds = useMemo(() => {
    if (!isPreviewMode) return visibleSortableIds;
    return previewFlatItems
      .filter((item) => {
        if (item.type === 'line-item' && item.projectId !== null && collapsedProjects.has(item.projectId)) {
          return false;
        }
        return true;
      })
      .map((item) => item.id);
  }, [isPreviewMode, previewFlatItems, collapsedProjects, visibleSortableIds]);

  const displayedRates: Rates = previewingVersion
    ? {
        taxRate: (previewingVersion.snapshot as any).taxRate ?? DEFAULT_TAX_RATE,
        markupRate: (previewingVersion.snapshot as any).markupRate ?? DEFAULT_MARKUP_RATE,
      }
    : jobRates;

  // Live-compute a line item total from its sub-items — avoids stale cached totalCost
  // Uses displayedRates so version preview shows totals with the version's rates, not current rates
  const computeItemTotal = useCallback(
    (subItems: SubItemForm[]) => calculateTotalCost(subItems, displayedRates, typeMeta, categories),
    [displayedRates, typeMeta, categories],
  );

  // Find the selected line item for columns view detail panel
  const selectedItem = useMemo((): LineItemData | undefined => {
    if (!selectedItemId) return undefined;
    if (isPreviewMode) {
      const looseItem = displayedLineItems.find((item) => item.id === selectedItemId);
      if (looseItem) return looseItem;
      for (const project of displayedProjects) {
        const projectItem = project.lineItems.find((item) => item.id === selectedItemId);
        if (projectItem) return projectItem;
      }
      return undefined;
    }
    const found = flatItems.find(i => i.type === 'line-item' && i.id === selectedItemId);
    return found ? (found.data as LineItemData) : undefined;
  }, [selectedItemId, isPreviewMode, displayedLineItems, displayedProjects, flatItems]);

  // Render accordion body for expanded line items
  const renderAccordionBody = useCallback((item: LineItemData) => {
    if (item.category.toLowerCase() === 'disclaimer') return null;
    if (item.category === 'Carpet Cleaning') {
      // Carpet drawer edits target `item.id` explicitly — the accordion body
      // can be expanded without the line item being the globally-selected one,
      // so relying on `selectedItemId` (what handleSubItemsChange does) would
      // update the wrong row or no-op.
      return (
        <CarpetLineItemBody
          subItems={item.subItems}
          onSubItemsChange={(subItems) => {
            markDirty();
            const totalCost = calculateTotalCost(subItems, jobRates, typeMeta, categories);
            flatUpdateLineItem(item.id, (li) => ({ ...li, subItems, totalCost }));
          }}
          readOnly={isPreviewMode}
        />
      );
    }
    if (item.category === 'Equipment Rental') {
      return (
        <EquipmentLineItemBody
          subItems={item.subItems}
          onSubItemsChange={(subItems) => {
            markDirty();
            const totalCost = calculateTotalCost(subItems, jobRates, typeMeta, categories);
            flatUpdateLineItem(item.id, (li) => ({ ...li, subItems, totalCost }));
          }}
          readOnly={isPreviewMode}
          lineItemDescription={item.description ?? null}
        />
      );
    }
    return (
      <div className="space-y-3">
        {!isPreviewMode && (
          <div className="w-44">
            <Combobox
              value=""
              onValueChange={(value) => {
                if (value) {
                  handleAddSubItem(value as string);
                }
              }}
              options={getSubItemTypeOptions(categories)}
              placeholder={
                <span className="inline-flex items-center gap-1.5">
                  <PlusCircle className="h-4 w-4" />
                  Add item
                </span>
              }
              searchPlaceholder="Search..."
              emptyText="No type found."
            />
          </div>
        )}
        <LineItemDetail
          item={item}
          onSubItemsChange={handleSubItemsChange}
          readOnly={isPreviewMode}
          taxRate={displayedRates.taxRate}
          markupRate={displayedRates.markupRate}
          roundingPrecision={isPreviewMode ? undefined : roundingPrecision}
          onRemoveRounding={isPreviewMode ? undefined : () => handleSetRounding(null)}
          selectedSubItemId={selectedSubItemId}
          onSelectSubItem={setSelectedSubItemId}
        />
      </div>
    );
  }, [isPreviewMode, categories, typeMeta, handleAddSubItem, handleSubItemsChange, displayedRates, jobRates, markDirty, flatUpdateLineItem, selectedSubItemId]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">
          Error loading breakdown: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-visible">
      {/* Save error banner */}
      {saveError && (
        <SaveErrorBanner
          error={saveError}
          isSaving={isSaving}
          onRetry={save}
          onDownloadBackup={() => downloadBackup(jobCode)}
        />
      )}
      {/* Document banner (flag-on path only) */}
      {currentDocSummary && (
        <div className="mb-3">
          <DocumentBanner
            name={currentDocSummary.name}
            isActive={currentDocSummary.isActive}
            onRename={() => setRenameDialogOpen(true)}
          />
        </div>
      )}
      {/* View toggle header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg bg-gray-200 p-1">
            <button
              onClick={() => setViewMode('columns')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'columns'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Columns2 className="h-4 w-4" />
              Column View
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
              List View
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Table className="h-4 w-4" />
              Table View
            </button>
          </div>
          <span className="text-gray-300">|</span>
          <JobRatesDisplay
            rates={displayedRates}
            onRatesChange={!isPreviewMode ? handleRatesChange : undefined}
          />
          {!isPreviewMode && (
            <DropdownButtonMenu
              label="Round Prices"
              ariaLabel="Round prices"
              disabled={flatItems.length === 0}
              items={[
                { label: 'Round to nearest $0.10', onClick: () => handleSetRounding(0.1) },
                { label: 'Round to nearest $1.00', onClick: () => handleSetRounding(1) },
              ]}
            />
          )}
        </div>

        {/* Save status / Preview mode indicator + Versions dropdown */}
        <div className="flex items-center gap-3 text-sm">
          {previewingVersion ? (
            <PreviewVersionChip
              versionName={previewingVersion.name}
              createdAt={previewingVersion.createdAt}
              onRestore={() => setRestoreDialogOpen(true)}
              onExit={handleExitPreview}
            />
          ) : (
            <SaveStatusIndicator
              isOnline={isOnline}
              isSaving={isSaving}
              isDirty={isDirty}
              hasOfflineEdits={hasOfflineEdits}
              hasContent={flatItems.length > 0}
              onSave={save}
            />
          )}
          {!isPreviewMode && (
            <UndoRedoButtons
              canUndo={undoHistory.canUndo}
              canRedo={undoHistory.canRedo}
              onUndo={handleUndo}
              onRedo={handleRedo}
            />
          )}
          {documentId ? (
            <VersionSwitcher
              jobId={jobId}
              slotKey={slotKey}
              documentId={documentId}
              onNavigate={handleNavigateDocId}
              disabled={isSaving || isDirty}
              jobProjectType={jobProjectType}
              getContents={() => flatItemsToContents(flatItems, jobRates, roundingPrecision)}
            />
          ) : (
            <VersionDropdown
              jobId={jobId}
              activeVersionId={previewingVersion?.id ?? activeBreakdownVersionId}
              activeVersionName={versionLabel}
              onSelectVersion={handleSelectVersion}
              onVersionSaved={handleVersionSaved}
              disabled={isPreviewMode || isSaving || isDirty}
            />
          )}
        </div>
      </div>

      {viewMode === 'list' && (
        <div className="flex flex-col flex-1 min-h-0 overflow-visible">
          <Panel
            title={
              !isPreviewMode ? (
                <BreakdownAddActions
                  onAddLineItem={handleAddLineItem}
                  onAddProject={handleAddProject}
                  onAddTemplateItem={handleAddTemplateItem}
                  usedTemplateIds={usedTemplateIds}
                  commonItems={commonItems}
                  fullTemplates={fullTemplates}
                  onApplyFullTemplate={handleSelectFullTemplate}
                  jobProjectType={jobProjectType}
                  checkedCount={checkedIds.size}
                  onCopy={handleCopy}
                  onPaste={handlePaste}
                  isPreviewMode={isPreviewMode}
                  copyPasteLabels
                  selectAllChecked={selectAllState.isChecked}
                  selectAllIndeterminate={selectAllState.isIndeterminate}
                  selectAllDisabled={displayedLineItems.length === 0 && displayedProjects.length === 0}
                  onSelectAll={handleSelectAll}
                />
              ) : (
                <span className="text-sm font-medium text-gray-600">Line Items</span>
              )
            }
            className="flex-1 flex flex-col min-h-0"
            contentClassName="flex-1 min-h-0 space-y-4 overflow-auto pb-4"
            headerAction={
              <DropdownButtonMenu
                label="Generate Reports"
                ariaLabel="Generate reports"
                disabled={displayedLineItems.length === 0 && displayedProjects.length === 0}
                separator={canCreateInvoices ? [1, 3] : 1}
                items={[
                  { label: isExportingPdf === 'summary' ? 'Downloading...' : 'Estimate Summary', onClick: () => handleExportPdf('summary'), disabled: isExportingPdf !== null },
                  { label: isExportingPdf === 'full' ? 'Downloading...' : 'Full Report (with details)', onClick: () => handleExportPdf('full'), disabled: isExportingPdf !== null },
                  { label: isExportingExcel ? 'Preparing...' : 'Preview Job Card', onClick: handlePreviewJobCard, disabled: isExportingExcel },
                  { label: isExportingExcel ? 'Preparing...' : 'Generate Job Card', onClick: handleExportExcel, disabled: isExportingExcel },
                  ...(canCreateInvoices
                    ? [
                        { label: 'Invoice (Final)', onClick: handleCreateFinalInvoice },
                        { label: 'Invoice (Partial)', onClick: () => setPartialDialogOpen(true) },
                      ]
                    : []),
                ]}
              />
            }
          >
            <BreakdownDndWrapper
              flatItems={displayedFlatItems}
              visibleSortableIds={displayedVisibleSortableIds}
              collapsedProjects={collapsedProjects}
              selectedItemId={selectedItemId}
              expandedItemId={selectedItemId}
              autoFocusItemId={autoFocusItemId}
              onAutoFocusComplete={() => setAutoFocusItemId(null)}
              onSelectItem={handleToggleExpand}
              onDeleteLineItem={handleDeleteLineItem}
              onCategoryChange={handleLineItemCategoryChange}
              onDescriptionChange={handleLineItemDescriptionChange}
              onItemDateChange={handleLineItemDateChange}
              onDragEnd={(activeId, overItem, isCollapsedProject) => {
                undoHistory.markDirty({ flatItems, jobRates, roundingPrecision });
                const result = moveItem(activeId, overItem, isCollapsedProject);
                if (result) {
                  const patches = getDndPatches(result.prev, result.next);
                  persistGranular(patches);
                }
              }}
              onProjectDragStart={startProjectDrag}
              onProjectDragEnd={(activeId, overItem) => {
                undoHistory.markDirty({ flatItems, jobRates, roundingPrecision });
                const result = moveProject(activeId, overItem);
                if (result) {
                  const patches = getDndPatches(result.prev, result.next);
                  persistGranular(patches);
                }
              }}
              onProjectDragCancel={cancelProjectDrag}
              onToggleCollapse={toggleCollapse}
              onRenameProject={handleRenameProject}
              onDeleteProject={handleDeleteProject}
              onAddLineItemToProject={handleAddLineItemToProject}
              renderAccordionBody={renderAccordionBody}
              readOnly={isPreviewMode}
              checkedIds={!isPreviewMode ? checkedIds : undefined}
              onCheckItem={!isPreviewMode ? handleCheckItem : undefined}
              onCheckProject={!isPreviewMode ? handleCheckProject : undefined}
              getProjectCheckState={!isPreviewMode ? getProjectCheckState : undefined}
              computeItemTotal={computeItemTotal}
              useRingHighlight
            />
            {(displayedLineItems.length > 0 || displayedProjects.length > 0) && (
              <div className="flex justify-between items-center px-3 py-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-700">Total</span>
                <span className="text-sm font-semibold text-gray-900">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                    displayedLineItems.reduce((sum, item) => sum + computeItemTotal(item.subItems), 0) +
                    displayedProjects.reduce((sum, p) => sum + p.lineItems.reduce((s, item) => s + computeItemTotal(item.subItems), 0), 0)
                  )}
                </span>
              </div>
            )}
          </Panel>
        </div>
      )}

      {viewMode === 'columns' && (
        <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0 overflow-visible">
          {/* Left column - Line items list */}
          <div className="w-full lg:w-1/2 flex flex-col min-h-0">
            <Panel
              title={
                !isPreviewMode ? (
                  <BreakdownAddActions
                    onAddLineItem={handleAddLineItem}
                    onAddProject={handleAddProject}
                    onAddTemplateItem={handleAddTemplateItem}
                    usedTemplateIds={usedTemplateIds}
                    commonItems={commonItems}
                    fullTemplates={fullTemplates}
                    onApplyFullTemplate={handleSelectFullTemplate}
                    jobProjectType={jobProjectType}
                    checkedCount={checkedIds.size}
                    onCopy={handleCopy}
                    onPaste={handlePaste}
                    isPreviewMode={isPreviewMode}
                    selectAllChecked={selectAllState.isChecked}
                    selectAllIndeterminate={selectAllState.isIndeterminate}
                    selectAllDisabled={displayedLineItems.length === 0 && displayedProjects.length === 0}
                    onSelectAll={handleSelectAll}
                  />
                ) : (
                  <span className="text-sm font-medium text-gray-600">Line Items</span>
                )
              }
              className="flex-1 flex flex-col min-h-0"
              contentClassName="flex-1 min-h-0 space-y-4 overflow-auto pb-4"
              headerAction={
                <DropdownButtonMenu
                  label="Generate Reports"
                  ariaLabel="Generate reports"
                  disabled={displayedLineItems.length === 0 && displayedProjects.length === 0}
                  separator={1}
                  items={[
                    { label: isExportingPdf === 'summary' ? 'Downloading...' : 'Estimate Summary', onClick: () => handleExportPdf('summary'), disabled: isExportingPdf !== null },
                    { label: isExportingPdf === 'full' ? 'Downloading...' : 'Full Report (with details)', onClick: () => handleExportPdf('full'), disabled: isExportingPdf !== null },
                    { label: isExportingExcel ? 'Preparing...' : 'Preview Job Card', onClick: handlePreviewJobCard, disabled: isExportingExcel },
                    { label: isExportingExcel ? 'Preparing...' : 'Generate Job Card', onClick: handleExportExcel, disabled: isExportingExcel },
                  ]}
                />
              }
            >
              <BreakdownDndWrapper
                flatItems={displayedFlatItems}
                visibleSortableIds={displayedVisibleSortableIds}
                collapsedProjects={collapsedProjects}
                selectedItemId={selectedItemId}
                expandedItemId={selectedItemId}
                autoFocusItemId={autoFocusItemId}
                onAutoFocusComplete={() => setAutoFocusItemId(null)}
                onSelectItem={handleToggleExpand}
                onDeleteLineItem={handleDeleteLineItem}
                onCategoryChange={handleLineItemCategoryChange}
                onDescriptionChange={handleLineItemDescriptionChange}
                onItemDateChange={handleLineItemDateChange}
                onDragEnd={(activeId, overItem, isCollapsedProject) => {
                  undoHistory.markDirty({ flatItems, jobRates, roundingPrecision });
                  const result = moveItem(activeId, overItem, isCollapsedProject);
                  if (result) {
                    const patches = getDndPatches(result.prev, result.next);
                    persistGranular(patches);
                  }
                }}
                onProjectDragStart={startProjectDrag}
                onProjectDragEnd={(activeId, overItem) => {
                  undoHistory.markDirty({ flatItems, jobRates, roundingPrecision });
                  const result = moveProject(activeId, overItem);
                  if (result) {
                    const patches = getDndPatches(result.prev, result.next);
                    persistGranular(patches);
                  }
                }}
                onProjectDragCancel={cancelProjectDrag}
                onToggleCollapse={toggleCollapse}
                onRenameProject={handleRenameProject}
                onDeleteProject={handleDeleteProject}
                onAddLineItemToProject={handleAddLineItemToProject}
                readOnly={isPreviewMode}
                checkedIds={!isPreviewMode ? checkedIds : undefined}
                onCheckItem={!isPreviewMode ? handleCheckItem : undefined}
                onCheckProject={!isPreviewMode ? handleCheckProject : undefined}
                getProjectCheckState={!isPreviewMode ? getProjectCheckState : undefined}
                computeItemTotal={computeItemTotal}
                useRingHighlight
              />
              {(displayedLineItems.length > 0 || displayedProjects.length > 0) && (
                <div className="flex justify-between items-center px-3 py-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Total</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                      displayedLineItems.reduce((sum, item) => sum + computeItemTotal(item.subItems), 0) +
                      displayedProjects.reduce((sum, p) => sum + p.lineItems.reduce((s, item) => s + computeItemTotal(item.subItems), 0), 0)
                    )}
                  </span>
                </div>
              )}
            </Panel>
          </div>

          {/* Right column - Cost breakdown detail panel */}
          <div className="flex-1 min-h-0 flex flex-col">
            {selectedItem && selectedItem.category.toLowerCase() !== 'disclaimer' ? (
              <div className="flex-1 flex flex-col min-h-0 bg-white rounded-lg shadow">
                <div className="p-6 flex-1 flex flex-col min-h-0">
                  {selectedItem.category === 'Carpet Cleaning' ? (
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-4">
                      <CarpetLineItemBody
                        subItems={selectedItem.subItems}
                        onSubItemsChange={handleSubItemsChange}
                        readOnly={isPreviewMode}
                      />
                    </div>
                  ) : selectedItem.category === 'Equipment Rental' ? (
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-4">
                      <EquipmentLineItemBody
                        subItems={selectedItem.subItems}
                        onSubItemsChange={handleSubItemsChange}
                        readOnly={isPreviewMode}
                        lineItemDescription={selectedItem.description ?? null}
                      />
                    </div>
                  ) : (
                    <>
                      {!isPreviewMode && (
                        <div className="flex items-center mb-4">
                          <div className="w-44 rounded-md">
                            <Combobox
                              value=""
                              onValueChange={(value) => {
                                if (value) {
                                  handleAddSubItem(value as string);
                                }
                              }}
                              options={getSubItemTypeOptions(categories)}
                              placeholder={
                                <span className="inline-flex items-center gap-1.5">
                                  <PlusCircle className="h-4 w-4" />
                                  Add item
                                </span>
                              }
                              searchPlaceholder="Search..."
                              emptyText="No type found."
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex-1 min-h-0 space-y-4 overflow-y-auto overflow-x-hidden pb-4">
                        <LineItemDetail
                          item={selectedItem}
                          onSubItemsChange={handleSubItemsChange}
                          readOnly={isPreviewMode}
                          taxRate={displayedRates.taxRate}
                          markupRate={displayedRates.markupRate}
                          roundingPrecision={isPreviewMode ? undefined : roundingPrecision}
                          onRemoveRounding={isPreviewMode ? undefined : () => handleSetRounding(null)}
                          selectedSubItemId={selectedSubItemId}
                          onSelectSubItem={setSelectedSubItemId}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <p className="text-sm text-gray-400">Select a line item to view its cost breakdown</p>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'table' && (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <Panel
            title={
              !isPreviewMode ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleAddLineItem}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    Add Line Item
                  </button>
                  <TemplatePopover
                    onAddItem={handleAddTemplateItem}
                    usedTemplateIds={usedTemplateIds}
                    commonItems={commonItems}
                    fullTemplates={fullTemplates}
                    onApplyFullTemplate={handleSelectFullTemplate}
                    jobProjectType={jobProjectType}
                  />
                  {checkedIds.size > 0 && (
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy ({checkedIds.size})
                    </button>
                  )}
                  <button
                    onClick={handlePaste}
                    disabled={isPreviewMode}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-700"
                  >
                    <ClipboardPaste className="h-3.5 w-3.5" />
                    Paste
                  </button>
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-600">Line Items</span>
              )
            }
            className="flex-1 flex flex-col min-h-0 overflow-hidden"
            contentClassName="flex-1 min-h-0 overflow-auto pb-4"
            headerAction={
              <DropdownButtonMenu
                label="Generate Reports"
                ariaLabel="Generate reports"
                disabled={displayedLineItems.length === 0}
                separator={canCreateInvoices ? [1, 3] : 1}
                items={[
                  { label: isExportingPdf === 'summary' ? 'Downloading...' : 'Estimate Summary', onClick: () => handleExportPdf('summary'), disabled: isExportingPdf !== null },
                  { label: isExportingPdf === 'full' ? 'Downloading...' : 'Full Report (with details)', onClick: () => handleExportPdf('full'), disabled: isExportingPdf !== null },
                  { label: isExportingExcel ? 'Preparing...' : 'Preview Job Card', onClick: handlePreviewJobCard, disabled: isExportingExcel },
                  { label: isExportingExcel ? 'Preparing...' : 'Generate Job Card', onClick: handleExportExcel, disabled: isExportingExcel },
                  ...(canCreateInvoices
                    ? [
                        { label: 'Invoice (Final)', onClick: handleCreateFinalInvoice },
                        { label: 'Invoice (Partial)', onClick: () => setPartialDialogOpen(true) },
                      ]
                    : []),
                ]}
              />
            }
          >
            <BreakdownTableView
              lineItems={displayedLineItems}
              projects={displayedProjects}
              onLineItemsChange={(items) => {
                markDirty();
                // Replace all loose line items in flatItems with the new items from table
                const nonLooseItems = flatItems.filter(i => i.type === 'project-header' || i.projectId !== null);
                const newFlatItems: FlatItem[] = [
                  ...items.map(item => ({ id: item.id, type: 'line-item' as const, projectId: null, data: item })),
                  ...nonLooseItems,
                ];
                initFlatItems(newFlatItems);
              }}
              onDeleteLineItem={handleDeleteLineItem}
              readOnly={isPreviewMode}
              taxRate={displayedRates.taxRate}
              markupRate={displayedRates.markupRate}
              checkedIds={!isPreviewMode ? checkedIds : undefined}
              onCheckItem={!isPreviewMode ? handleCheckItem : undefined}
              selectAllChecked={selectAllState.isChecked}
              selectAllIndeterminate={selectAllState.isIndeterminate}
              selectAllDisabled={displayedLineItems.length === 0 && displayedProjects.length === 0}
              onSelectAll={!isPreviewMode ? handleSelectAll : undefined}
              expandedIds={expandedItemIds}
              onToggleExpand={(id) => {
                setExpandedItemIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                });
              }}
            />
          </Panel>
        </div>
      )}

      {/* Restore Version Confirm Dialog */}
      {previewingVersion && (
        <RestoreConfirmDialog
          jobId={jobId}
          versionId={previewingVersion.id}
          versionName={previewingVersion.name}
          open={restoreDialogOpen}
          onOpenChange={setRestoreDialogOpen}
          onSuccess={handleRestoreSuccess}
          hasUnsavedChanges={isDirty}
        />
      )}

      <RecoverEditsDialog
        open={pendingRecovery !== null}
        onRecover={acceptRecovery}
        onDiscard={discardRecovery}
      />

      <PartialInvoiceDialog
        open={partialDialogOpen}
        onOpenChange={setPartialDialogOpen}
        jobId={jobId}
        jobCode={jobCode}
        jobDescription={null}
      />

      <FinalInvoiceDialog
        open={finalDialogOpen}
        onOpenChange={setFinalDialogOpen}
        jobId={jobId}
        jobCode={jobCode}
        jobDescription={null}
      />

      <ApplyTemplateConfirm
        open={!!pendingTemplate}
        onOpenChange={(o) => {
          if (!o) setPendingTemplate(null);
        }}
        templateName={pendingTemplate?.name ?? ''}
        templateHasRates={pendingTemplate ? snapshotHasRates(pendingTemplate.snapshot) : false}
        onApply={(mode) => {
          if (pendingTemplate) applyFullTemplate(pendingTemplate, mode);
        }}
      />

      <RenameDocumentDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        jobId={jobId}
        document={currentDocSummary ?? null}
      />

      {/* Previews the doc the user is viewing, not the slot's active one. */}
      <JobCardPreviewModal
        jobId={jobId}
        jobCode={jobCode}
        documentId={documentId}
        open={jobCardPreviewOpen}
        onOpenChange={setJobCardPreviewOpen}
      />

    </div>
  );
}
